import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import parse from "html-react-parser";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Questions } from "../../../Models";
import {
  isFollowUpRoleQuestion,
  isMultipleRoleQuestion,
  isOptionsRoleQuestion,
  isTextRoleQuestion,
} from "../../../api/roleApplicationScoring.api";

export type RoleQuestionOption = { id: string; text: string };

type Props = {
  questions: Questions[];
  optionsByQuestion: Record<string, RoleQuestionOption[]>;
  /** answerId -> weight as typed. Blank means the option carries no weight. */
  weights: Record<string, string>;
  onWeightChange: (answerId: string, value: string) => void;
  /** questionId -> points for a free-text question, already defaulted. Blank means not scored. */
  textWeights: Record<string, string>;
  onTextWeightChange: (questionId: string, value: string) => void;
  relevantThreshold: string;
  goodThreshold: string;
  onThresholdChange: (which: "relevant" | "good", value: string) => void;
  thresholdError: string | null;
};

const weightInputClass =
  "w-20 h-7 rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-2 text-right text-xs text-gray-900 dark:text-gray-200 focus:ring-0";

/**
 * Weights for a role's application questions, plus the cut-offs that turn the resulting score
 * into Not relevant / Relevant / Good.
 *
 * Shown inline as a one-line summary; the full editor opens in a popup, so a role with many
 * questions doesn't push the rest of the form down the page. Edits go straight into the role
 * form's state and are saved with the role - the popup itself saves nothing.
 *
 * Options questions weight each option. Free-text questions carry one weight for the whole
 * question - earned in full when answered, 0 when left empty. Number questions aren't scored.
 */
export default function RoleQuestionWeightsEditor({
  questions,
  optionsByQuestion,
  weights,
  onWeightChange,
  textWeights,
  onTextWeightChange,
  relevantThreshold,
  goodThreshold,
  onThresholdChange,
  thresholdError,
}: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (questions.length === 0) return null;

  const scorable = questions.filter(
    (q) => isOptionsRoleQuestion(q.tags) || isTextRoleQuestion(q.tags)
  );
  const unscoredCount = questions.length - scorable.length;

  // How many questions currently carry at least one weight - shown in the summary line.
  const weightedCount = scorable.filter((q) =>
    isTextRoleQuestion(q.tags)
      ? (textWeights[q.id] ?? "").trim() !== ""
      : (optionsByQuestion[q.id] ?? []).some((o) => (weights[o.id] ?? "").trim() !== "")
  ).length;

  const renderEditor = () => (
    <>
      {scorable.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          None of the selected questions can be weighted.
        </p>
      ) : (
        <div className="space-y-4">
          {scorable.map((q) => {
            const isText = isTextRoleQuestion(q.tags);
            const multiple = !isText && isMultipleRoleQuestion(q.tags);
            const isFollowUp = isFollowUpRoleQuestion(q);
            const options = optionsByQuestion[q.id];

            return (
              <div key={q.id}>
                <div className="flex flex-wrap items-baseline gap-2 mb-1.5">
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                    {parse(q.text)}
                  </span>
                  {isText && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      (text answer - full points if answered, 0 if left empty)
                    </span>
                  )}
                  {multiple && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      (multiple choice - points add up)
                    </span>
                  )}
                  {isFollowUp && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      (follow-up - only counted for candidates who are shown it)
                    </span>
                  )}
                </div>

                {isText ? (
                  <label className="flex items-center justify-between gap-3 text-xs text-gray-700 dark:text-gray-300 sm:w-1/2">
                    <span>Points if answered</span>
                    <input
                      type="number"
                      min={0}
                      max={1000}
                      step="any"
                      inputMode="decimal"
                      placeholder="-"
                      value={textWeights[q.id] ?? ""}
                      onChange={(e) => onTextWeightChange(q.id, e.target.value)}
                      className={weightInputClass}
                    />
                  </label>
                ) : options === undefined ? (
                  <p className="text-xs text-gray-400 italic">Loading options...</p>
                ) : options.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">This question has no options yet.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {options.map((opt) => (
                      <label
                        key={opt.id}
                        className="flex items-center justify-between gap-3 text-xs text-gray-700 dark:text-gray-300"
                      >
                        <span className="truncate" title={opt.text}>
                          {opt.text}
                        </span>
                        <input
                          type="number"
                          min={0}
                          max={1000}
                          step="any"
                          inputMode="decimal"
                          placeholder="-"
                          value={weights[opt.id] ?? ""}
                          onChange={(e) => onWeightChange(opt.id, e.target.value)}
                          className={weightInputClass}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {unscoredCount > 0 && (
        <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
          {unscoredCount} number question{unscoredCount === 1 ? "" : "s"} not shown - number
          answers aren't scored.
        </p>
      )}
    </>
  );

  return (
    <div className="mt-4">
      {/* Inline summary - the whole editor lives in the popup. */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="text-xs">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Answer weights</span>
          <span className="ml-2 text-gray-500 dark:text-gray-400">
            {weightedCount} of {scorable.length} question{scorable.length === 1 ? "" : "s"} weighted
            {" · "}Relevant from {relevantThreshold || "-"}% · Good from {goodThreshold || "-"}%
          </span>
          {thresholdError && (
            <span className="ml-2 text-red-600 dark:text-red-400">{thresholdError}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md bg-white dark:bg-transparent px-3 py-1.5 text-xs font-semibold text-entntblue ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Configure
        </button>
      </div>

      {/* Portalled to <body>: the role form sits inside a sliding panel whose CSS transform makes
          `position: fixed` relative to the panel instead of the viewport, so an in-place modal
          opened halfway down a tall form and had to be scrolled to. At <body> it's centred on
          screen wherever the form is scrolled. Still a React child, so state flows as normal. */}
      {open && createPortal(
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setOpen(false)}
            />
            <div className="relative w-full max-w-2xl bg-white dark:bg-darkbglight rounded-lg shadow-xl text-left">
              <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300">
                    Answer weights
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Give each answer points. A candidate's score is the points they earned as a
                    percentage of the points available to them, and decides their category.
                    Candidates never see these.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto">{renderEditor()}</div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-700 dark:text-gray-300">
                  <label className="flex items-center gap-2">
                    Relevant from
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={relevantThreshold}
                      onChange={(e) => onThresholdChange("relevant", e.target.value)}
                      className="w-16 h-7 rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-2 text-right text-xs text-gray-900 dark:text-gray-200 focus:ring-0"
                    />
                    %
                  </label>
                  <label className="flex items-center gap-2">
                    Good from
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={goodThreshold}
                      onChange={(e) => onThresholdChange("good", e.target.value)}
                      className="w-16 h-7 rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-2 text-right text-xs text-gray-900 dark:text-gray-200 focus:ring-0"
                    />
                    %
                  </label>
                  <span className="text-gray-500 dark:text-gray-400">
                    Below "Relevant" is Not relevant.
                  </span>
                </div>
                {thresholdError && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400">{thresholdError}</p>
                )}
                <div className="mt-4 flex items-center justify-between gap-3">
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Changes are saved with the role when you click Save / Update.
                  </p>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md bg-entntblue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
